import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { api } from '../api/endpoints';
import type { Notification } from '../api/types';
import { useMe } from '../auth/context';
import { Icon } from '../components/icons';
import { fmtDateTime } from '../ops/format';
import { ago, notificationIcon } from '../ops/ticketView';
import { useProject } from '../projects/context';

// NotificationsBell: Notification Center – Zusammenfassung je Typ („3 neue Rückfragen“) und die
// letzten Benachrichtigungen mit Direktlink auf den Vorgang (auch in anderen Projekten).
export function NotificationsBell() {
  const { project } = useProject();
  const me = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const count = useQuery({ queryKey: ['me', 'notifications', 'count'], queryFn: ({ signal }) => api.unreadCount(signal), refetchInterval: 30_000 });
  const summary = useQuery({ queryKey: ['me', 'notifications', 'summary'], queryFn: ({ signal }) => api.notificationSummary(signal), enabled: open });
  const list = useQuery({ queryKey: ['me', 'notifications', 'list'], queryFn: ({ signal }) => api.notifications({ limit: 20 }, signal), enabled: open });
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['me', 'notifications'] });
  const read = useMutation({ mutationFn: (id: string) => api.readNotification(id), onSuccess: invalidate });
  const readAll = useMutation({ mutationFn: () => api.readAllNotifications(), onSuccess: invalidate });
  const unread = count.data?.count ?? 0;
  const projectKey = (projectId: string | null) => me.projects.find((p) => p.id === projectId)?.key ?? project.key;

  const openItem = (n: Notification) => {
    if (!n.read_at) read.mutate(n.id);
    setOpen(false);
    if (n.link) navigate(`/p/${projectKey(n.project_id)}${n.link}`);
  };

  return (
    <div className="notifications">
      <button
        type="button"
        className="icon-btn bell"
        aria-label={unread ? `${unread} ungelesene Benachrichtigungen` : 'Benachrichtigungen'}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="bell" size={22} />
        {unread > 0 && <span className="bell-count">{unread > 99 ? '99+' : unread}</span>}
      </button>
      {open && (
        <div className="notification-panel" role="dialog" aria-label="Benachrichtigungen">
          <div className="notification-head">
            <strong>Benachrichtigungen</strong>
            {unread > 0 && (
              <button type="button" className="btn btn-small btn-ghost" onClick={() => readAll.mutate()}>
                Alle gelesen
              </button>
            )}
          </div>
          {(summary.data?.items.length ?? 0) > 0 && (
            <ul className="notification-summary" aria-label="Zusammenfassung">
              {summary.data?.items.map((s) => (
                <li key={`${s.type}-${s.project_id ?? ''}`}>
                  <strong>{s.count}</strong> {s.label}
                  {s.project_id && s.project_id !== project.id && <span className="muted"> · {me.projects.find((p) => p.id === s.project_id)?.name}</span>}
                </li>
              ))}
            </ul>
          )}
          {list.isPending ? (
            <p className="muted">Lädt …</p>
          ) : !list.data?.items.length ? (
            <p className="muted">Keine Benachrichtigungen.</p>
          ) : (
            <ul className="notification-list">
              {list.data.items.map((n) => (
                <li key={n.id} className={n.read_at ? 'read' : 'unread'}>
                  <button type="button" onClick={() => openItem(n)}>
                    <span className={`notification-icon type-${notificationIcon(n.type)}`} aria-hidden="true">
                      <Icon name={notificationIcon(n.type)} size={16} />
                    </span>
                    <span className="notification-text">
                      <strong>{n.title}</strong>
                      {n.body && <span className="notification-body">{n.body}</span>}
                      <time className="muted" dateTime={n.created_at} title={fmtDateTime(n.created_at)}>
                        {ago(n.created_at)}
                      </time>
                    </span>
                    {!n.read_at && <span className="unread-dot" aria-hidden="true" />}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="notification-foot">
            <Link to={`/p/${project.key}/profil#benachrichtigungen`} onClick={() => setOpen(false)}>
              Einstellungen
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
